import React, { useEffect, useRef, useState, useCallback } from 'react';
import styles from './NewPitch.module.css';

/* ─── Images ─── */
import img1 from '../images/בית של יוקרה וחומר 2.webp';
import img2 from '../images/בית מדברי מודרני 7.webp';
import img3 from '../images/בית של אור ועיצוב 1.webp';

/* ─── Data ─── */

const ROTATING = [
  'קניתם מגרש ולא יודעים מאיפה מתחילים?',
  'מתכננים תוספת בנייה ומפחדים מהבירוקרטיה?',
  'רוצים לשפץ אבל חוששים שהתקציב יברח?',
  'קיבלתם דירת קבלן ולא בטוחים מה עכשיו?',
];

const PAINS = [
  { icon: '⸺', text: 'עשרות החלטות - ואין מי שיגיד מה נכון' },
  { icon: '⸺', text: 'בעלי מקצוע שכל אחד מושך לכיוון אחר' },
  { icon: '⸺', text: 'תקציב שמתחיל לרוץ לפני שהתכנון נגמר' },
  { icon: '⸺', text: 'הפחד שהתוצאה לא תיראה כמו מה שדמיינתם' },
];

const ADVANTAGES = [
  {
    num: '01',
    title: 'כל שקל הולך למקום הנכון',
    desc: 'תכנון מדויק מראש חוסך שינויים יקרים באמצע. אתם יודעים מה התקציב - ואנחנו עובדים בתוכו.',
    img: img1,
  },
  {
    num: '02',
    title: 'הבירוקרטיה? עלינו.',
    desc: 'גרמושקה, ועדות, יועצים, היתרים - הכל מתנהל מול מי שמכירה את השפה. אתם מקבלים עדכון, לא כאב ראש.',
    img: img2,
  },
  {
    num: '03',
    title: 'אדריכלות ועיצוב, תחת קורת גג אחת',
    desc: 'בלי פערים בין מה שהמבנה דורש לבין מה שהעיצוב צריך. הכל מתואם מהיום הראשון.',
    img: img3,
  },
];

/* ─── Animated counter hook ─── */

function useCountUp(target, duration, shouldStart) {
  const [value, setValue] = useState(0);
  const rafRef = useRef(null);

  useEffect(() => {
    if (!shouldStart) return;
    let start = null;
    const step = (ts) => {
      if (!start) start = ts;
      const elapsed = ts - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.round(eased * target));
      if (progress < 1) {
        rafRef.current = requestAnimationFrame(step);
      }
    };
    rafRef.current = requestAnimationFrame(step);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [shouldStart, target, duration]);

  return value;
}

/* ─── Scroll progress hook ─── */

function useScrollProgress(ref) {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (!ref.current) return;
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        if (!ref.current) { ticking = false; return; }
        const rect = ref.current.getBoundingClientRect();
        const viewH = window.innerHeight;
        const total = rect.height + viewH;
        const scrolled = viewH - rect.top;
        setProgress(Math.max(0, Math.min(1, scrolled / total)));
        ticking = false;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [ref]);

  return progress;
}

/* ─── Main component ─── */

function NewPitch() {
  const sectionRef = useRef(null);
  const refs = useRef({});
  const [seen, setSeen] = useState(() => new Set());
  const [rotIdx, setRotIdx] = useState(0);

  const scrollProgress = useScrollProgress(sectionRef);
  const statValue = useCountUp(75, 2200, seen.has('stat'));

  const register = useCallback((key) => (el) => {
    if (!el) return;
    el.dataset.key = key;
    refs.current[key] = el;
  }, []);

  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            const key = e.target.dataset.key;
            setSeen((prev) => {
              if (prev.has(key)) return prev;
              const next = new Set(prev);
              next.add(key);
              return next;
            });
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    );
    const raf = requestAnimationFrame(() => {
      Object.values(refs.current).forEach((el) => el && io.observe(el));
    });
    return () => { cancelAnimationFrame(raf); io.disconnect(); };
  }, []);

  useEffect(() => {
    const id = setInterval(() => {
      setRotIdx((i) => (i + 1) % ROTATING.length);
    }, 3500);
    return () => clearInterval(id);
  }, []);

  return (
    <section id="pitch" className={styles.section} ref={sectionRef}>
      <div className={styles.grain} aria-hidden="true" />

      {/* Vertical progress line */}
      <div className={styles.progressTrack} aria-hidden="true">
        <span
          className={styles.progressFill}
          style={{ transform: `scaleY(${scrollProgress})` }}
        />
      </div>

      {/* ——— Part 1: The Hook ——— */}
      <div
        ref={register('hook')}
        className={`${styles.hook} ${seen.has('hook') ? styles.seen : ''}`}
      >
        <span className={styles.eyebrow}>
          — רגע, בואו נדבר על זה בגובה העיניים —
        </span>
        <div className={styles.rotatingWrap} aria-live="polite">
          {ROTATING.map((q, i) => (
            <p
              key={i}
              className={`${styles.rotatingQ} ${i === rotIdx ? styles.rotatingActive : ''}`}
            >
              {q}
            </p>
          ))}
        </div>
      </div>

      {/* ——— Part 2: Empathy + Pain pills ——— */}
      <div
        ref={register('empathy')}
        className={`${styles.empathy} ${seen.has('empathy') ? styles.seen : ''}`}
      >
        <p className={styles.empathyText}>
          זה לגמרי מובן. בנייה של בית זה אחד הדברים הכי גדולים שתעשו בחיים -
          ופתאום צצים דברים שלא חשבתם עליהם:
        </p>

        <div className={styles.painGrid}>
          {PAINS.map((pain, i) => (
            <div
              key={i}
              className={`${styles.painPill} ${seen.has('empathy') ? styles.painVisible : ''}`}
              style={{ '--i': i }}
            >
              <span className={styles.painLine} aria-hidden="true" />
              <span className={styles.painText}>{pain.text}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ——— Part 3: THE KILLER STAT — full width dramatic ——— */}
      <div
        ref={register('stat')}
        className={`${styles.statSection} ${seen.has('stat') ? styles.seen : ''}`}
      >
        <div className={styles.statInner}>
          <div className={styles.statLeft}>
            <span className={styles.statNumWrap}>
              <span className={styles.statNum} aria-label="75%">
                {statValue}
              </span>
              <span className={styles.statPercent}>%</span>
            </span>
            <span className={styles.statUnderline} aria-hidden="true" />
          </div>

          <div className={styles.statRight}>
            <h3 className={styles.statTitle}>
              מפרויקטי הבנייה והשיפוץ בישראל חורגים מהתקציב.
            </h3>
            <p className={styles.statDesc}>
              ורובם גם מתעכבים. חוסר ודאות, לחץ ותסכול -
              כמעט תמיד בגלל סיבה אחת:<br />
              <strong>חוסר תכנון מקצועי.</strong>
            </p>
          </div>
        </div>

        {/* Decorative image strip behind stat */}
        <div className={styles.statImageStrip} aria-hidden="true">
          <div className={styles.statImg}>
            <img src={img1} alt="" loading="lazy" />
          </div>
          <div className={styles.statImg}>
            <img src={img2} alt="" loading="lazy" />
          </div>
          <div className={styles.statImg}>
            <img src={img3} alt="" loading="lazy" />
          </div>
        </div>
      </div>

      {/* ——— Part 4: The turn ——— */}
      <div
        ref={register('turn')}
        className={`${styles.turn} ${seen.has('turn') ? styles.seen : ''}`}
      >
        <span className={styles.turnRule} aria-hidden="true" />
        <h2 className={styles.turnTitle}>
          <span className={styles.turnBut}>אבל</span> זה לא חייב להיות ככה.
        </h2>
        <p className={styles.turnLead}>
          כשיש לצדכם אדריכלית שמכירה את התהליך על בוריו - שמתכננת, מלווה
          היתרים, מפקחת בשטח ומעצבת את הפנים - הכל משתנה. לא כי זה קל, אלא כי
          יש מישהי שעברה את זה עשרות פעמים ויודעת בדיוק לאן ללכת.
        </p>
      </div>

      {/* ——— Part 5: Advantages with images ——— */}
      <div className={styles.advSection}>
        {ADVANTAGES.map((adv, i) => (
          <div
            key={i}
            ref={register(`adv${i}`)}
            className={`${styles.advCard} ${seen.has(`adv${i}`) ? styles.seen : ''} ${i % 2 === 1 ? styles.advCardAlt : ''}`}
            style={{ '--i': i }}
          >
            {/* Image side */}
            <div className={styles.advImageWrap}>
              <img
                src={adv.img}
                alt=""
                className={styles.advImage}
                loading="lazy"
                decoding="async"
              />
              <span className={styles.advImageTint} aria-hidden="true" />
            </div>

            {/* Text side */}
            <div className={styles.advTextSide}>
              <span className={styles.advWatermark} aria-hidden="true">
                {adv.num}
              </span>
              <span className={styles.advNum}>{adv.num}</span>
              <h3 className={styles.advTitle}>{adv.title}</h3>
              <p className={styles.advDesc}>{adv.desc}</p>
            </div>
          </div>
        ))}
      </div>

      {/* ——— Part 6: Closing quote ——— */}
      <div
        ref={register('close')}
        className={`${styles.closer} ${seen.has('close') ? styles.seen : ''}`}
      >
        <span className={styles.closerMark} aria-hidden="true">&ldquo;</span>
        <p className={styles.closerQuote}>
          אתם שמים את כל כספכם ואת כל חלום חייכם על מישהו -
          <span className={styles.closerEm}> תוודאו שזו הבחירה הנכונה.</span>
        </p>
        <span className={styles.closerMarkEnd} aria-hidden="true">&rdquo;</span>
      </div>
    </section>
  );
}

export default NewPitch;
